import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, QrCode, Download, Unlock, Lock, Edit2, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { QRCodeCanvas as QRCode } from 'qrcode.react';
import apiClient, { getAuthToken } from '@/utils/api';
import { jwtDecode } from 'jwt-decode';

const TablesPage = () => {
  const navigate = useNavigate();
  const [tables, setTables] = useState([]);
  const [newTableNumber, setNewTableNumber] = useState('');
  const [newTableCapacity, setNewTableCapacity] = useState('4');
  const [showAddDialog, setShowAddDialog] = useState(false);
  
  const [showOpenDialog, setShowOpenDialog] = useState(false);
  const [selectedTableToOpen, setSelectedTableToOpen] = useState(null);
  const [showCapacityDialog, setShowCapacityDialog] = useState(false);
  const [maxDevices, setMaxDevices] = useState('');

  const [userPermissions, setUserPermissions] = useState([]);
  const [reservationEnabled, setReservationEnabled] = useState(localStorage.getItem('reservation_enabled') === 'true');

  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      navigate('/login');
      return;
    }
    try {
      const decoded = jwtDecode(token);
      let perms = decoded.permissions || ['orders'];
      if (decoded.user_type === 'owner') {
        setUserPermissions(['all']);
      } else {
        const expandedPerms = new Set(perms);
        if (perms.includes('tables')) {
          expandedPerms.add('view_tables').add('manage_table_status').add('add_table').add('delete_table');
        }
        setUserPermissions(Array.from(expandedPerms));
      }
    } catch (e) {
      console.error(e);
    }
    fetchTables();
    apiClient.get('/tenant/settings').then(res => {
      const isEnabled = res.data.reservation_enabled ?? false;
      localStorage.setItem('reservation_enabled', isEnabled ? 'true' : 'false');
      setReservationEnabled(isEnabled);
    }).catch(() => {});
    const interval = setInterval(fetchTables, 5000);
    return () => clearInterval(interval);
  }, [navigate]);

  const hasPermission = (p) => userPermissions.includes('all') || userPermissions.includes(p);

  const fetchTables = async () => {
    try {
      const response = await apiClient.get('/tables');
      setTables(response.data);
    } catch (error) {
      toast.error('Masalar yüklenirken hata oluştu');
    }
  };

  const handleAddTable = async () => {
    if (!newTableNumber) {
      toast.error('Lütfen masa numarası girin');
      return;
    }

    try {
      await apiClient.post('/tables', { 
        table_number: parseInt(newTableNumber),
        capacity: parseInt(newTableCapacity) || 4
      });
      toast.success('Masa başarıyla eklendi');
      setNewTableNumber('');
      setNewTableCapacity('4');
      setShowAddDialog(false);
      fetchTables();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Masa eklenirken hata oluştu');
    }
  };

  const handleUnreserveTable = async (tableNumber) => {
    if (!window.confirm('Bu masanın rezervasyonunu kaldırmak istediğinize emin misiniz?')) return;
    try {
      await apiClient.put(`/tables/${tableNumber}/unreserve`);
      toast.success('Masa rezervasyonu kaldırıldı, masa artık müsait');
      fetchTables();
    } catch (error) {
      toast.error('Rezervasyon kaldırılırken hata oluştu');
    }
  };

  const handleDeleteTable = async (tableId) => {
    if (!window.confirm('Bu masayı silmek istediğinize emin misiniz?')) return;

    try {
      await apiClient.delete(`/tables/${tableId}`);
      toast.success('Masa silindi');
      fetchTables();
    } catch (error) {
      toast.error('Masa silinirken hata oluştu');
    }
  };

  const handleOpenTable = async () => {
    if (!selectedTableToOpen) return;
    try {
      await apiClient.put(`/tables/${selectedTableToOpen.table_number}/open`, { max_devices: parseInt(maxDevices) || 2 });
      toast.success(`Masa ${selectedTableToOpen.table_number} açıldı`);
      setShowOpenDialog(false);
      fetchTables();
    } catch (error) {
      toast.error('Masa açılırken hata oluştu');
    }
  };

  const handleUpdateCapacity = async () => {
    if (!selectedTableToOpen) return;
    try {
      await apiClient.put(`/tables/${selectedTableToOpen.table_number}/capacity`, { max_devices: parseInt(maxDevices) || 2 });
      toast.success(`Masa ${selectedTableToOpen.table_number} kapasitesi güncellendi`);
      setShowCapacityDialog(false);
      fetchTables();
    } catch (error) {
      toast.error('Kapasite güncellenirken hata oluştu');
    }
  };

  const handleCloseTable = async (tableNumber) => {
    if (!window.confirm('Masayı kapatmak istediğinize emin misiniz?')) return;
    try {
      await apiClient.put(`/tables/${tableNumber}/close`);
      toast.success('Masa kapatıldı');
      fetchTables();
    } catch (error) {
      toast.error('Masa kapatılırken hata oluştu');
    }
  };

  const handleDismissWaiter = async (tableNumber) => {
    try {
      await apiClient.put(`/tables/${tableNumber}/dismiss-waiter`);
      toast.success('Çağrı kapatıldı');
      fetchTables();
    } catch (error) {
      toast.error('Çağrı kapatılırken hata oluştu');
    }
  };

  const downloadQR = (table) => {
    const canvas = document.getElementById(`qr-hidden-${table.qr_code}`);
    if (canvas) {
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `masa-${table.table_number}-qr.png`;
      link.href = url;
      link.click();
      toast.success(`Masa ${table.table_number} QR kodu indirildi.`);
    } else {
      toast.error('QR kod bulunamadı.');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Masa Yönetimi
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mt-1">Masaları yönetin, açın ve kapatın</p>
          </div>
          {hasPermission('add_table') && (
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
              <Button
                data-testid="add-table-button"
                className="bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full"
              >
                <Plus size={20} className="mr-2" />
                Yeni Masa
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
              <DialogHeader>
                <DialogTitle className="text-slate-900 dark:text-slate-100">Yeni Masa Ekle</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Masa Numarası</label>
                  <Input
                    data-testid="table-number-input"
                    type="number"
                    placeholder="Masa Numarası"
                    value={newTableNumber}
                    onChange={(e) => setNewTableNumber(e.target.value)}
                    className="mt-1 bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-200"
                  />
                </div>
                {reservationEnabled && (
                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Masa Kapasitesi (Kişi Sayısı)</label>
                    <Input
                      type="number"
                      placeholder="Kaç Kişilik (Örn: 4)"
                      value={newTableCapacity}
                      onChange={(e) => setNewTableCapacity(e.target.value)}
                      className="mt-1 bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-200"
                    />
                    <p className="text-xs text-slate-500 mt-1">Online masa rezervasyon sisteminde müşterilere gösterilecek kişi sayısı.</p>
                  </div>
                )}
                <Button
                  data-testid="confirm-add-table"
                  onClick={handleAddTable}
                  className="w-full bg-orange-500 hover:bg-orange-600"
                >
                  Ekle
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          )}

          {/* Masa Açma Modalı */}
          <Dialog open={showOpenDialog} onOpenChange={setShowOpenDialog}>
            <DialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
              <DialogHeader>
                <DialogTitle className="text-slate-900 dark:text-slate-100">Masa {selectedTableToOpen?.table_number} Aç</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <p className="text-slate-600 dark:text-slate-400 text-sm">Kaç kişinin telefonundan menüye girilmesine (sipariş verilmesine) izin verilecek?</p>
                <Input
                  type="number"
                  min="1"
                  placeholder="Kişi Sayısı (Örn: 2)"
                  value={maxDevices}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    if (val > 0 || e.target.value === '') setMaxDevices(e.target.value);
                  }}
                  className="bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-200"
                />
                <Button
                  onClick={handleOpenTable}
                  className="w-full bg-green-500 hover:bg-green-600"
                >
                  Masayı Aç
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Kapasite Güncelleme Modalı */}
          <Dialog open={showCapacityDialog} onOpenChange={setShowCapacityDialog}>
            <DialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
              <DialogHeader>
                <DialogTitle className="text-slate-900 dark:text-slate-100">Masa {selectedTableToOpen?.table_number} Kapasiteyi Güncelle</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <p className="text-slate-600 dark:text-slate-400 text-sm">Masaya bağlanabilecek yeni kişi (cihaz) sayısını girin:</p>
                <Input
                  type="number"
                  min="1"
                  placeholder="Kişi Sayısı (Örn: 3)"
                  value={maxDevices}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    if (val > 0 || e.target.value === '') setMaxDevices(e.target.value);
                  }}
                  className="bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-200"
                />
                <Button
                  onClick={handleUpdateCapacity}
                  className="w-full bg-blue-500 hover:bg-blue-600"
                >
                  Güncelle
                </Button>
              </div>
            </DialogContent>
          </Dialog>

        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {tables.map((table) => (
            <Card
              key={table.id}
              data-testid={`table-card-${table.table_number}`}
              className={`bg-white dark:bg-slate-900/50 p-6 flex flex-col h-full transition-colors border ${table.waiter_called ? 'border-red-500 shadow-[0_0_15px_rgba(239,68,68,0.5)]' : (table.is_open ? 'border-green-500/50 hover:border-green-500' : 'border-slate-200 dark:border-slate-800 hover:border-orange-500/30')}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100">Masa {table.table_number}</h3>
                  {table.waiter_called && (
                    <Bell className="text-red-500 animate-bounce" size={20} />
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {reservationEnabled && table.is_reserved && (
                    <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 border font-bold animate-pulse">
                      REZERVELİ
                    </Badge>
                  )}
                  <Badge className={`${table.is_open ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-slate-500/20 text-slate-600 dark:text-slate-400 border-slate-500/30'} border`}>
                    {table.is_open ? 'AÇIK' : 'KAPALI'}
                  </Badge>
                </div>
              </div>

              {reservationEnabled && (
                <div className="mb-3 px-3 py-1 bg-slate-100 dark:bg-slate-800 rounded-md text-xs text-slate-600 dark:text-slate-400 font-medium flex justify-between items-center">
                  <span>Kapasite</span>
                  <span className="font-bold text-slate-800 dark:text-slate-200">{table.capacity || 4} Kişilik</span>
                </div>
              )}

              {table.is_open ? (
                <div className="mb-4 text-center p-3 bg-slate-50 dark:bg-slate-950 rounded-lg border border-slate-200 dark:border-slate-800 relative">
                  <Button
                    onClick={() => {
                      setSelectedTableToOpen(table);
                      setMaxDevices(table.max_devices?.toString() || '2');
                      setShowCapacityDialog(true);
                    }}
                    className="absolute top-2 right-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 w-7 h-7 p-0"
                    title="Kapasiteyi Güncelle"
                  >
                    <Edit2 size={12} />
                  </Button>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Bağlı Cihazlar</p>
                  <p className="text-lg font-mono font-bold text-slate-800 dark:text-slate-200">
                    {(table.connected_devices || []).length} / {table.max_devices}
                  </p>
                </div>
              ) : (
                <div className="bg-white p-4 rounded-lg mb-4 flex justify-center items-center overflow-hidden">
                  <QRCode
                    value={`https://cularix.com/?qr=${table.qr_code}`}
                    size={140}
                    level="H"
                    style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                  />
                </div>
              )}

              {/* Gizli yüksek çözünürlüklü QR Kod (Sadece indirme işlemi için) */}
              <div style={{ display: 'none' }}>
                <QRCode
                  id={`qr-hidden-${table.qr_code}`}
                  value={`https://cularix.com/?qr=${table.qr_code}`}
                  size={1024}
                  level="H"
                />
              </div>

              <div className="flex gap-2 flex-wrap mt-auto pt-4">
                {table.waiter_called && (
                  <Button
                    onClick={() => handleDismissWaiter(table.table_number)}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white animate-pulse"
                  >
                    İsteği Kapat
                  </Button>
                )}
                {hasPermission('manage_table_status') && table.is_open && !table.waiter_called && (
                  <Button
                    onClick={() => handleCloseTable(table.table_number)}
                    className="flex-1 bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white"
                  >
                    <Lock size={16} className="mr-2" />
                    Kapat
                  </Button>
                )}
                {hasPermission('manage_table_status') && !table.is_open && !table.waiter_called && (
                  <Button
                    onClick={() => {
                      setSelectedTableToOpen(table);
                      setShowOpenDialog(true);
                    }}
                    className="flex-1 bg-green-500/10 text-green-500 hover:bg-green-500 hover:text-white"
                  >
                    <Unlock size={16} className="mr-2" />
                    Aç
                  </Button>
                )}
                {reservationEnabled && table.is_reserved && (
                  <Button
                    onClick={() => handleUnreserveTable(table.table_number)}
                    className="flex-1 bg-purple-500 hover:bg-purple-600 text-white font-bold text-xs py-1 px-2"
                    title="Rezervasyonu Kaldır"
                  >
                    Rezervi Kaldır
                  </Button>
                )}
                
                <Button
                  data-testid={`download-qr-${table.table_number}`}
                  onClick={() => downloadQR(table)}
                  className="bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200"
                  size="icon"
                >
                  <Download size={16} />
                </Button>
                {hasPermission('delete_table') && (
                  <Button
                    data-testid={`delete-table-${table.table_number}`}
                    onClick={() => handleDeleteTable(table.id)}
                    variant="destructive"
                    size="icon"
                  >
                    <Trash2 size={16} />
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>

        {tables.length === 0 && (
          <Card className="bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 p-12 text-center">
            <QrCode size={48} className="mx-auto text-slate-600 mb-4" />
            <p className="text-slate-600 dark:text-slate-400">Henüz masa eklenmemiş</p>
            <p className="text-slate-500 text-sm mt-2">Başlamak için yukarıdaki butona tıklayın</p>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default TablesPage;